import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useOptiBayTestPaths as useTestPaths } from '@/hooks/useOptiBayTaxonomy';
import { useSessionTestPaths, useBulkSetSessionTestPaths } from '@/hooks/useOptiBayTestPaths';
import type { OptiBaySession } from '@/types/optibay';
import { OptiBayTrainingHints } from './OptiBayTrainingHints';
import { cn } from '@/lib/utils';

interface OptiBayTestPathStepProps {
  session: OptiBaySession;
  onNext: () => void;
  onBack: () => void;
}

export function OptiBayTestPathStep({ session, onNext, onBack }: OptiBayTestPathStepProps) {
  const { data: availablePaths = [], isLoading } = useTestPaths(
    session.system_family_id || null,
  );
  const { data: selectedPaths = [] } = useSessionTestPaths(session.id);
  const bulkSet = useBulkSetSessionTestPaths();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  // Sync from DB on load
  useEffect(() => {
    if (selectedPaths.length > 0) {
      setSelectedIds(new Set(selectedPaths.map(p => p.test_path_id)));
    }
  }, [selectedPaths]);

  const filtered = availablePaths.filter(p =>
    !search || p.label.toLowerCase().includes(search.toLowerCase()) ||
    (p.description && p.description.toLowerCase().includes(search.toLowerCase()))
  );

  const togglePath = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleContinue = async () => {
    await bulkSet.mutateAsync({
      sessionId: session.id,
      testPathIds: Array.from(selectedIds),
    });
    onNext();
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-6 text-center">Loading test paths…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Select Diagnostic Tests</h4>
        <Badge variant="secondary" className="text-xs">
          {selectedIds.size} selected
        </Badge>
      </div>

      {availablePaths.length > 5 && (
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search tests…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
      )}

      <div className="space-y-1 max-h-64 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">
            {availablePaths.length === 0
              ? 'No test paths defined for this system family yet.'
              : 'No matching tests found.'}
          </p>
        ) : (
          filtered.map(tp => (
            <label
              key={tp.id}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                selectedIds.has(tp.id)
                  ? 'border-primary bg-primary/5'
                  : 'hover:bg-accent/50'
              )}
            >
              <Checkbox
                checked={selectedIds.has(tp.id)}
                onCheckedChange={() => togglePath(tp.id)}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{tp.label}</p>
                {tp.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{tp.description}</p>
                )}
              </div>
            </label>
          ))
        )}
      </div>

      <OptiBayTrainingHints session={session} className="mt-2" />

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back
        </Button>
        <Button
          size="sm"
          onClick={handleContinue}
          disabled={selectedIds.size === 0 || bulkSet.isPending}
        >
          Continue to Evidence
          <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
